import { useRef, useState } from "react";
import { Upload, Link2, Loader2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const ACCEPT = ".mp3,.mp4,.wav,.m4a,.webm,audio/*,video/*";
const MAX = 100 * 1024 * 1024;

const PLATFORM_LABELS = {
    youtube: "YouTube",
    tiktok: "TikTok",
    instagram: "Instagram",
    facebook: "Facebook",
    twitter: "X",
};

function detectPlatform(url) {
    const u = url.toLowerCase();
    if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
    if (u.includes("tiktok.com")) return "tiktok";
    if (u.includes("instagram.com")) return "instagram";
    if (u.includes("facebook.com") || u.includes("fb.watch") || u.includes("fb.com")) return "facebook";
    if (u.includes("x.com") || u.includes("twitter.com")) return "twitter";
    return null;
}

function fmtMin(sec) {
    if (!sec) return "?";
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function UploadZone({ onCreated, compact = false }) {
    const [url, setUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [busyLabel, setBusyLabel] = useState("");
    const [drag, setDrag] = useState(false);
    const [confirmInfo, setConfirmInfo] = useState(null);
    const fileRef = useRef(null);

    const handleFile = async (file) => {
        if (!file) return;
        if (file.size > MAX) {
            toast.error("File too large", { description: "Max 100MB" });
            return;
        }
        setBusy(true);
        setBusyLabel("Uploading & transcribing…");
        const fd = new FormData();
        fd.append("file", file);
        try {
            const res = await api.post("/transcripts/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Transcript ready");
            onCreated?.(res.data);
        } catch (e) {
            toast.error("Upload failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setBusy(false);
            setBusyLabel("");
        }
    };

    const runTranscribe = async (linkUrl) => {
        const platform = detectPlatform(linkUrl);
        const label = platform ? `Fetching from ${PLATFORM_LABELS[platform] || platform}…` : "Transcribing…";
        setBusyLabel(label);
        setBusy(true);
        try {
            const res = await api.post("/transcripts/url", { url: linkUrl });
            toast.success("Transcript ready");
            setUrl("");
            onCreated?.(res.data);
        } catch (e) {
            toast.error("Transcription failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setBusy(false);
            setBusyLabel("");
            setConfirmInfo(null);
        }
    };

    const handleUrl = async () => {
        const linkUrl = url.trim();
        if (!linkUrl) return;
        const platform = detectPlatform(linkUrl);

        // For social URLs, do a cheap preview to warn on long videos
        if (platform && platform !== "twitter") {
            setBusy(true);
            setBusyLabel(`Checking ${PLATFORM_LABELS[platform]} link…`);
            try {
                const preview = await api.post("/transcripts/preview", { url: linkUrl });
                setBusy(false);
                setBusyLabel("");
                const p = preview.data;
                if (p.would_exceed_quota || (p.duration_minutes && p.duration_minutes > 5)) {
                    setConfirmInfo({ ...p, url: linkUrl });
                    return;
                }
                // Safe — proceed
                runTranscribe(linkUrl);
            } catch (e) {
                setBusy(false);
                setBusyLabel("");
                toast.error("Could not fetch link info", { description: e?.response?.data?.detail || e.message });
            }
        } else {
            runTranscribe(linkUrl);
        }
    };

    return (
        <div className="space-y-4" data-testid="upload-zone">
            <div
                className={`flex items-center gap-2 rounded-2xl border bg-white pl-4 pr-2 py-2 shadow-sm transition-all ${
                    drag ? "border-indigo-500 ring-4 ring-indigo-500/10 bg-indigo-50/30" : "border-slate-200 focus-within:border-indigo-500 focus-within:ring-4 focus-within:ring-indigo-500/10"
                }`}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDrag(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f) handleFile(f);
                }}
            >
                <Link2 className="w-5 h-5 text-slate-400 shrink-0" strokeWidth={1.75} />
                <input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !busy && handleUrl()}
                    disabled={busy}
                    placeholder="Paste a YouTube, TikTok, Instagram, Facebook, or audio URL — or drop a file"
                    className="flex-1 bg-transparent border-0 outline-none text-base placeholder:text-slate-400 py-2"
                    data-testid="url-input"
                />
                <button
                    onClick={() => fileRef.current?.click()}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
                    data-testid="upload-btn"
                >
                    <Upload className="w-4 h-4" strokeWidth={1.75} />
                    <span className="hidden sm:inline">Upload</span>
                </button>
                <button
                    onClick={handleUrl}
                    disabled={busy || !url.trim()}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="transcribe-btn"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Transcribe"}
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept={ACCEPT}
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="file-input"
                />
            </div>

            {/* Live status while busy */}
            {busy && busyLabel && (
                <div className="flex items-center justify-center gap-2 text-sm text-slate-500" data-testid="busy-label">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>{busyLabel}</span>
                </div>
            )}

            {/* Long-video confirmation modal */}
            {confirmInfo && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" data-testid="quota-warning-modal">
                    <div className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-xl overflow-hidden fade-in-up">
                        {confirmInfo.thumbnail && (
                            <img src={confirmInfo.thumbnail} alt="" className="w-full h-40 object-cover" />
                        )}
                        <div className="p-6">
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
                                    <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={1.75} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-base font-semibold text-slate-900">
                                        {confirmInfo.would_exceed_quota ? "This will exceed your monthly limit" : "Heads up — long video"}
                                    </h3>
                                    {confirmInfo.title && (
                                        <p className="mt-1 text-sm text-slate-600 truncate">{confirmInfo.title}</p>
                                    )}
                                    <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                                        <div className="rounded-lg bg-slate-50 px-3 py-2">
                                            <div className="text-slate-400">Length</div>
                                            <div className="font-medium text-slate-900 mt-0.5">{fmtMin(confirmInfo.duration_seconds)}</div>
                                        </div>
                                        <div className="rounded-lg bg-slate-50 px-3 py-2">
                                            <div className="text-slate-400">You have left</div>
                                            <div className={`font-medium mt-0.5 ${confirmInfo.would_exceed_quota ? "text-red-600" : "text-slate-900"}`}>
                                                {confirmInfo.minutes_remaining} min
                                            </div>
                                        </div>
                                    </div>
                                    {confirmInfo.would_exceed_quota && (
                                        <p className="mt-3 text-sm text-red-600">
                                            Transcription will be rejected after the API call. We recommend a shorter clip.
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="mt-6 flex items-center justify-end gap-2">
                                <button
                                    onClick={() => setConfirmInfo(null)}
                                    className="inline-flex items-center justify-center rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors"
                                    data-testid="quota-cancel-btn"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => runTranscribe(confirmInfo.url)}
                                    disabled={confirmInfo.would_exceed_quota}
                                    className="inline-flex items-center justify-center rounded-xl bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    data-testid="quota-continue-btn"
                                >
                                    Transcribe anyway
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!compact && (
                <p className="text-center text-sm text-slate-400">
                    Supports YouTube, TikTok, Instagram, Facebook & direct files (mp3, mp4, wav, m4a, webm) · up to 100MB · 99+ languages auto-detected
                </p>
            )}
        </div>
    );
}
